// app/frontend/components/EventForm/sections/ArtistSelectionSection.tsx
import React from 'react';
import { useState, useCallback } from 'react';
import { Accordion, Group, Text, MultiSelect, Tooltip, ActionIcon, Stack, Avatar } from '@mantine/core';
import { IconUsersGroup, IconInfoCircle } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import { ArtistSelectionSectionProps } from '../types';

interface Artist {
  id: number;
  name: string;
  avatar_url?: string;
}

const ArtistSelectionSection: React.FC<ArtistSelectionSectionProps> = ({
  form,
  artists,
  isLoadingArtists,
  artistOptionsLoaded,
  fetchAllArtists,
}) => {
  const [tooltipOpened, setTooltipOpened] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<Artist[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Debounce search input to avoid too many API calls
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);

  // Search for artists in the database
  const searchArtists = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
      const response = await fetch(`/artists/search?q=${encodeURIComponent(query)}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.artists || []);
      }
    } catch (error) {
      console.error('Error searching artists:', error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Effect to trigger search when debounced value changes
  React.useEffect(() => {
    searchArtists(debouncedSearch);
  }, [debouncedSearch, searchArtists]);

  // Combine initial artists with search results
  const availableArtists = React.useMemo(() => {
    if (searchValue.length >= 2) {
      // When searching, show search results
      return searchResults;
    } else {
      // When not searching, show initial artists (including selected ones)
      return artists;
    }
  }, [artists, searchResults, searchValue]);

  // Get selected artists for avatar display
  const selectedArtists = React.useMemo(() => {
    if (!form.values.artistIds || form.values.artistIds.length === 0) {
      return [];
    }

    return form.values.artistIds
      .map((id: number) => {
        // First try to find in available artists
        let artist = availableArtists.find((a) => a.id === id);

        // If not found and we have initial artists, search there too
        if (!artist) {
          artist = artists.find((a) => a.id === id);
        }

        return artist;
      })
      .filter(Boolean) as Artist[];
  }, [form.values.artistIds, availableArtists, artists]);

  return (
    <Accordion multiple className="form-accordion">
      <Accordion.Item value="artist-info">
        <Accordion.Control>
          <Group gap="xs">
            <IconUsersGroup size={28} />
            <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Artists</Text>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <MultiSelect
            label={
              <Group gap={4} align="center">
                <Text className="mb-2">Select Artists</Text>
                <Tooltip
                  label="Search for any artist in the database. Type at least 2 characters to search."
                  multiline
                  w={280}
                  withArrow
                  position="bottom"
                  color="gray.9"
                >
                  <ActionIcon
                    variant="transparent"
                    size="sm"
                    className="mb-2"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setTooltipOpened(true)}
                    onMouseLeave={() => setTooltipOpened(false)}
                  >
                    <IconInfoCircle size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            }
            name="artistIds"
            hidePickedOptions
            disabled={isLoadingArtists}
            comboboxProps={{ shadow: 'md' }}
            placeholder={
              isSearching
                ? 'Searching...'
                : form.values.artistIds && form.values.artistIds.length > 0
                  ? ''
                  : 'Type to search for artists...'
            }
            styles={{
              label: { color: '#FFF7ED', fontSize: '16px' },
              input: { fontSize: '16px' },
              dropdown: { fontSize: '16px' },
            }}
            data={availableArtists.map((artist) => ({
              value: artist.id.toString(),
              label: artist.name,
            }))}
            value={(form.values.artistIds || []).map(String)}
            onChange={(values) => {
              const numericValues = Array.isArray(values) ? values.map(Number) : [];
              form.setFieldValue('artistIds', numericValues);
            }}
            searchable
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            clearable
            onDropdownOpen={() => {
              // Load initial artists if not loaded
              if (!artistOptionsLoaded) {
                fetchAllArtists();
              }
            }}
            nothingFoundMessage={
              searchValue.length >= 2 ? 'No artists found matching your search' : 'Type at least 2 characters to search'
            }
            maxDropdownHeight={300}
          />

          {/* Display selected artist avatars below */}
          {selectedArtists.length > 0 && (
            <Stack mt="md">
              <Text size="sm" c="dimmed">
                Selected Artists:
              </Text>
              <Group style={{ gap: '8px', flexWrap: 'wrap' }}>
                {selectedArtists.map((artist) => (
                  <Group key={artist.id} gap="xs" style={{ alignItems: 'center' }}>
                    <Avatar
                      src={artist.avatar_url}
                      alt={artist.name}
                      radius="50%"
                      size="lg"
                      style={{
                        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)',
                        border: '2px solid white',
                      }}
                    />
                    <Text size="sm" c="white" style={{ fontWeight: 500 }}>
                      {artist.name}
                    </Text>
                  </Group>
                ))}
              </Group>
            </Stack>
          )}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

export default ArtistSelectionSection;
